import React, { useEffect, useMemo, useState } from 'react'
import st from '../Goals/Goals.module.scss'
import styles from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import { useHistory } from 'react-router-dom'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { ReactComponent as Setting } from 'components/CRM/UI/icons/settings.svg'
import GoalsChart from './GoalsChart/GoalsChart'
import { superAxiosRequest } from 'axiosApi'
import SettingsGoals from './SettingsGoals/SettingsGoals'
import SelectCustom from 'components/ContentBox/Global/Select/SelectCustom'
import ToggleCheckbox from 'components/UI/ToggleCheckbox/ToggleCheckbox'
import { ReactComponent as ArrowLeft } from 'components/CRM/UI/icons/arrow-left.svg'
import { ReactComponent as ArrowRight } from 'components/CRM/UI/icons/arrow-right.svg'
import Loader from 'components/UI/Loader/Loader'
import { observer } from 'mobx-react'

const Goals = () => {
  const history = useHistory()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore

  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'crm_event',
    dict: {
      goals: 'Goals',
      by_budget: 'By budget',
      by_quantity: 'By quantity',
      set_up_goal: 'Set up goal',
      filter: 'Filter',
      total: 'Total',
      days: 'days',
      out_of: 'out of',
      no_goal_set: 'No goal set',
      target: 'Target',
      setting_goals: 'Setting goals',
      quarter: 'Quarter',
      activate_goals: 'Activate goals',
      save_settings: 'Save settings',
      budget: 'Budget',
      quantity: 'Quantity',
      manager: 'Manager',
      location: 'Location',
      month: 'Month',
      date_picker: 'Start & Finish Date',
    },
  })

  const periodicity = [
    { id: 1, label: translate.month, name: translate.month },
    { id: 2, label: translate.quarter, name: translate.quarter },
  ]

  const [filter, updateFilter] = useLocalStorageFilter({})
  const [loading, setLoading] = useState(false)
  const [goals, setGoals] = useState([])
  const [editMode, setEditMode] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const [monthDate, setMonthDate] = useState(new Date())
  const [selectedPeriod, setSelectedPeriod] = useState(1) // month | quarter
  const [periodRange, setPeriodRange] = useState(null)
  const [employees, setEmployees] = useState([])

  const mergeEmployeesAndGoals = (employees, goals) => {
    const goalMap = {}

    goals.forEach((goal) => {
      if (goal.employee && goal.employee.id) {
        goalMap[goal.employee.id] = goal
      }
    })

    return employees.map((emp) => {
      const existingGoal = goalMap[emp.id] || {}
      return {
        ...emp,
        branch: emp.branch,
        budget: emp.budget ?? existingGoal.by_budget ?? '',
        deals: emp.deals ?? existingGoal.by_quantity ?? '',
      }
    })
  }

  const mergedEmployees = useMemo(
    () => mergeEmployeesAndGoals(employees, goals),
    [employees, goals],
  )

  const dates = goals.map((item) => ({
    date_from: item.date_from,
    date_until: item.date_until,
  }))

  const handlePrev = () => {
    setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1))
  }

  const handleNext = () => {
    setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1))
  }

  const monthTranslations = {
    EN: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    KZ: [
      'Қаңтар',
      'Ақпан',
      'Наурыз',
      'Сәуір',
      'Мамыр',
      'Маусым',
      'Шілде',
      'Тамыз',
      'Қыркүйек',
      'Қазан',
      'Қараша',
      'Желтоқсан',
    ],
    RU: [
      'Январь',
      'Февраль',
      'Март',
      'Апрель',
      'Май',
      'Июнь',
      'Июль',
      'Август',
      'Сентябрь',
      'Октябрь',
      'Ноябрь',
      'Декабрь',
    ],
  }

  const formatMonthYear = (date) => {
    let dataStorage = localStorage.getItem('ud')
    let data = JSON.parse(dataStorage)

    let lang = data?.user_data?.profile?.localization

    if (!monthTranslations[lang]) {
      lang = 'EN'
    }

    const monthIndex = date.getMonth()
    const year = date.getFullYear()

    return `${monthTranslations[lang][monthIndex]} ${year}`
  }

  const monthNumber = monthDate.getMonth() + 1

  const toggleEditMode = () => {
    if (editMode) {
      saveSettings()
    }
    setEditMode(!editMode)
  }

  const transformData = (inputData) => {
    const branchMap = new Map()

    inputData.forEach((entry) => {
      const branchId = entry.branch?.id
      if (!branchId) return

      if (!branchMap.has(branchId)) {
        branchMap.set(branchId, {
          id: branchId,
          name: entry.branch.name || 'Без названия',
          managers: [],
        })
      }

      const manager = {
        id: entry.employee.id,
        name: entry.employee.name,
        budget: entry.by_budget,
        deals: entry.by_quantity,
        targetBudget: entry.progress?.deal_budget ?? 0,
        targetDeals: entry.progress?.deal_count ?? 0,
      }

      branchMap.get(branchId).managers.push(manager)
    })

    return Array.from(branchMap.values())
  }

  const transformedGoals = useMemo(() => transformData(goals), [goals])

  const branchOrder = useMemo(
    () => transformedGoals.map((group) => group.id),
    [transformedGoals],
  )

  const getPeriodRange = (currentMonth, type) => {
    const year = new Date().getFullYear()
    const monthIndex = Number(currentMonth) - 1

    if (monthIndex < 0 || monthIndex > 11 || isNaN(monthIndex)) {
      throw new Error("Invalid month. It should be between '01' and '12'")
    }

    let dateFrom, dateUntil

    if (type === 1) {
      dateFrom = new Date(year, monthIndex, 2)
      dateUntil = new Date(year, monthIndex + 1, 1) // Последний день текущего месяца
    } else if (type === 2) {
      dateFrom = new Date(year, monthIndex, 2)

      // Определяем последний месяц квартала
      let quarterEndMonth = monthIndex + 2
      let quarterEndYear = year

      if (quarterEndMonth > 11) {
        quarterEndMonth -= 12
        quarterEndYear += 1
      }

      dateUntil = new Date(quarterEndYear, quarterEndMonth + 1, 1) // Последний день третьего месяца квартала
    } else {
      throw new Error("Invalid type. Use 'month' or 'quarter'")
    }

    return {
      date_from: dateFrom.toISOString().split('T')[0],
      date_until: dateUntil.toISOString().split('T')[0],
    }
  }

  const saveSettings = async () => {
    setLoading(true)
    try {
      const period = getPeriodRange(String(monthNumber), selectedPeriod)
      await superAxiosRequest(
        {
          link: `goals`,
          method: 'post',
          crm: true,
        },
        mergedEmployees.map((emp) => ({
          branch_id: emp.branch ? emp.branch.id : null,
          employee_id: emp.id,
          date_from: period.date_from,
          date_until: period.date_until,
          by_budget: Number(emp.budget) || 0,
          by_quantity: Number(emp.deals) || 0,
        })),
      )

      await fetchGoals()
    } catch (e) {
      console.error('Ошибка при сохранении целей:', e)
    } finally {
      setLoading(false)
    }
  }

  const fetchGoals = async () => {
    setLoading(true)
    try {
      const { data } = await superAxiosRequest({
        link: `goals`,
        method: 'get',
        crm: true,
        params: {
          ...filter,
        },
      })
      setGoals(data)
    } catch (error) {
      console.error('Ошибка при запросе данных:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGoals()
  }, [filter])

  useEffect(() => {
    superAxiosRequest({
      link: `integration/employees`,
      method: 'get',
      crm: true,
      params: {
        limit: 1000,
      },
    })
      .then(({ data }) => {
        setEmployees(data.result || [])
      })
      .catch((error) => {
        console.error('Ошибка при получении сотрудников:', error)
      })
  }, [])

  useEffect(() => {
    setPeriodRange(getPeriodRange(monthNumber, selectedPeriod))
  }, [selectedPeriod, monthDate])

  const config = [
    {
      nameState: 'employee',
      sort_key: 'manager',
      placeholder: translate.manager,
      values: employees,
      option_label_key: 'full_name',
      type: 'select-multi',
      label: translate.manager,
    },
    {
      nameState: 'branch',
      sort_key: 'location',
      placeholder: translate.location,
      apiLinkCRM: 'integration/branches',
      type: 'select-multi',
      label: translate.location,
    },
    {
      nameState: 'by_budget',
      placeholder: translate.budget,
      type: 'input',
      label: translate.budget,
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: translate.date_picker,
    },
  ]

  useEffect(() => {
    setLoading(true)
  }, [])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== `/crm/goals`) {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  return (
    <div className={st.container}>
      <div className={st.header}>
        <div className={st.headerBlock}>
          <PageHeader
            title={`${editMode ? translate.setting_goals : translate.goals}`}
          />
          <button
            className={`${st.textName} ${st.settingsButton}`}
            onClick={toggleEditMode}
          >
            {editMode ? null : (
              <div className={st.filterIcon}>
                <Setting />
              </div>
            )}

            {editMode ? translate.save_settings : translate.set_up_goal}
          </button>
        </div>

        {!editMode && (
          <>
            <SearchBooks
              onSearch={(text) => {
                updateFilter({ ...filter, search: text })
                changeTextValue('search', {
                  key: 'search',
                  label: 'Search for client name',
                  value: text,
                })
              }}
              onClear={() => {
                const { name: text, ...obj } = filter
                updateFilter(obj)
                setTextValues(
                  filterTextValues.filter((field) => field.key !== 'search'),
                )
              }}
              value={filter?.search || ''}
              placeholder={'Search...'}
              translate={translate}
            />

            {isOpenFilter && (
              <AsideFilter
                translate={translate}
                getFilterParams={updateFilter}
                config={config}
                filterValue={filter}
                onDropFilter={() => {
                  updateFilter({})
                  closeFilter()
                }}
              />
            )}

            {!!filterTextValues.length && (
              <FilterTextValues
                onDelete={(key) => {
                  const { [key]: _, ...obj } = filter
                  updateFilter(obj)
                }}
              />
            )}
          </>
        )}
        {editMode && (
          <div className={st.containerSelect}>
            <div className={st.containerBox}>
              <div className={st.select}>
                <SelectCustom
                  options={periodicity}
                  valueId={selectedPeriod}
                  placeholder={
                    periodicity.find((op) => op.id === selectedPeriod)?.name ||
                    'Выберите период'
                  }
                  onChange={(e) => {
                    setSelectedPeriod(e.value)
                  }}
                />
              </div>
              <div className={st.counterBox}>
                <div onClick={handlePrev} className={st.button}>
                  <ArrowLeft />
                </div>
                <div className={st.monthInput}>
                  {formatMonthYear(monthDate)}
                </div>
                <div onClick={handleNext} className={st.button}>
                  <ArrowRight />
                </div>
              </div>
            </div>
            <div className={st.toggleCheckbox}>
              <ToggleCheckbox
                checked={!isDisabled}
                onChange={() => setIsDisabled(!isDisabled)}
                label={translate.activate_goals}
              />
            </div>
          </div>
        )}
      </div>
      {loading ? (
        <Loader />
      ) : editMode ? (
        <div className={st.containerClient}>
          <SettingsGoals
            translate={translate}
            disabled={isDisabled}
            employees={mergedEmployees}
            setEmployees={setEmployees}
            branchOrder={branchOrder}
          />
        </div>
      ) : Array.isArray(goals) && goals.length === 0 ? (
        <h1 className={styles.noData}>Нет данных для отображения!</h1>
      ) : (
        <div className={st.containerClient}>
          <GoalsChart
            dates={dates}
            translate={translate}
            goals={transformedGoals}
          />
        </div>
      )}
    </div>
  )
}

export default observer(Goals)
